import { html, render } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseComponent } from "../../base-component/base-component";
import { connect } from "pwa-helpers";
import store from "../../../store/store";
import { smallSpinner } from "../../../common/constants/api-constants";
import { CreateWishListRequest } from "../interfaces/wish-list-actions.interface";
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';
import { StoreState } from '../../../store/store.interface';
import { WishList } from '../interfaces/wish-list-items.interface';
import { addWishList } from "../thunks/wish-list-create.thunk";
import { resetCreateWishList, resetEditWishList } from "../store/wish-list.store";
import { editWishList } from "../thunks/wish-list-edit.thunk";
import { fetchAllWishLists } from '../thunks/wish-list-fetch-all-wish-lists.thunks';
import { getCreateWishListData, getEditWishListData } from '../store/wish-list.selector';
import { triggerCloseOffCanvas } from '../../off-canvas/store/off-canvas.store';
import { 
  isCreateWishListLoading, 
  isCreateWishListSuccess, 
  isEditWishListLoading,
  isEditWishListSuccess, 
} from "../utils/wish-list.util";
import {
  getGlobalToastContainer,
  renderCreateWishListSuccessToast,
  renderEditWishListSuccessToast,
  renderErrorToast
} from '../utils/wish-list-toast.util';
import "../../toast/toast";
import { fetchWishListByWishListId } from "../thunks/wish-list-fetch-wish-list-by-id.thunks";
import { focusOffCanvasElement, shouldClearOffCanvasElements } from '../../off-canvas/utils/off-canvas.util';
import { getOffCanvasData } from "../../off-canvas/store/off-canvas.selector";
import { WishListAnalytics } from "../utils/wish-list-analytics.util";
import { UserProfile } from "../../user-profile/user-profile.interface";
import { selectUser } from "../../user-profile/user-profile.selectors";

@customElement('mhecomm-create-wish-list')
export class CreateWishList extends connect(store)(BaseComponent) {

  @property({type: Boolean})
  isEditWishList = false;

  @property({type: Boolean})
  isWishListItemPage = false;

  @property({type: String})
  wishListLabel = 'List Name';

  @property({ type: Number })
  wishListId;

  @property({type: String})
  wishListName = '';

  @property({type: String})
  wishListButtonName = 'Add New List';

  @property()
  cancelButtonName = 'Cancel';

  /**
   * If the component is used as an off-canvas, this should be set to the off-canvas ID.
   */
  @property()
  offCanvasId = '';

  @state()
  isLoading = false;

  @state()
  showErrorMsg = false;

  @state()
  private isBtnDisabled = true;

  @state()
  private user: UserProfile | undefined;
  
  /**
   * The analytics instance for the wish list.
   */
  private readonly analytics: WishListAnalytics;

  constructor() {
    super();
    this.analytics = new WishListAnalytics();
  }

  stateChanged(state: StoreState): void {
    super.stateChanged(state);
    this.user = selectUser(state);
    this.handleOffCanvasActions(state);
    const wishList: WishList = getOffCanvasData(state)?.[this.offCanvasId];
    if (wishList && !isEditWishListLoading()) {
      this.wishListId = this.isEditWishList ? wishList.wishlist_id : undefined;
      this.wishListName = this.isEditWishList ? wishList.name : '';
    }

    isCreateWishListLoading() || isEditWishListLoading() 
      ? this.isLoading = true
      : this.isLoading = false;
  }

  renderComp() {
    return html`
      <div>
        <div class="pb-3">
          <div class="mb-3">
            <label 
              for=${this.getwishListId()} 
              class="form-label fw-normal mb-1">${this.wishListLabel}:
            </label>
            <input 
              type="text" 
              class="form-control rounded" 
              id=${this.getwishListId()}
              aria-label="wishlist name" 
              .value=${this.wishListName}
              @input=${(e) => this.handleWishListUpdate(e)} 
              placeholder="Name your list" 
              maxlength="40"
              ?disabled=${this.isLoading} 
            />
	        </div>
	      </div>
        <div class="d-flex flex-column border-top py-3">
          <button 
            id=${this.isEditWishList ? "cancelWishlistUpdationBtn" : "cancelWishlistCreationBtn"}
            class="btn btn-outline-primary mb-2" 
            aria-label="Cancel"
            @click=${this.onClickCancel}
          >
            ${this.cancelButtonName}
          </button>
          <button 
            class="btn btn-primary" 
            id=${this.isEditWishList ? "editWishListBtn" : "createWishlistBtn"}
            aria-label="Create wishlist"
            @click=${this.onClickCreateWishList} 
            data-automation-id=${this.isEditWishList ? "edit-wish-list-btn" : "create-wish-list-btn"}
            ?disabled=${this.isBtnDisabled || this.isLoading}
          >
            ${this.isLoading ? smallSpinner() : this.getButtonName()}
          </button>
        </div>
      </div>`
  }

  private getwishListId() {
    return this.isEditWishList ? "editWishListName" : "wishListName"
  }

  private getButtonName() {
    return this.isEditWishList ? 'Save' : 'Add New List';
  }

  private handleWishListUpdate(e: PointerEvent) {
    const inputEl = e.target as HTMLInputElement;
    this.wishListName = inputEl.value;
    this.isBtnDisabled = this.wishListName?.length < 1 ? true : false;
  }

  /**
   * Handled the click event for the cancel button.
   */
  private onClickCancel(): void {
    if (this.isEditWishList) {
      this.analytics.editWishList(this, this.wishListId, this.wishListName, this.user!, this.cancelButtonName, true);
    } else {
      this.analytics.createWishList(this, this.wishListId, this.wishListName,this.user!, this.cancelButtonName, true);
    }
    this.closeOffCanvasIfExist();
  }

  /**
   * Dispatches the action to create a wish list and closes the off-canvas if it is set.
   */
  private async onClickCreateWishList(): Promise<void> {
    if (this.isEditWishList) {
      this.analytics.editWishList(this, this.wishListId, this.wishListName, this.user!, this.getButtonName());
      return await store.dispatch(editWishList({requestBody: this.getContextParams(), wishListId: this.wishListId}))
        .then(this.handleApiResponse.bind(this))
        .catch(() => this.displayToast(true));
    } else {
      this.analytics.createWishList(this, this.wishListId, this.wishListName, this.user!, this.getButtonName());
      await store.dispatch(addWishList(this.getContextParams()))
        .then(this.handleApiResponse.bind(this))
        .catch(() => this.displayToast(true));
    }
  }

  private getContextParams(): CreateWishListRequest {
    return {wishlist: {name: this.wishListName, comments: ""}};
  }

  /**
   * Displays a toast depending on the wish list creation status.
   *
   * If the wish list creation was successful the wish lists will be refreshed.
   */
  private handleApiResponse(): void {
    if (this.isEditWishList ? !isEditWishListSuccess() : !isCreateWishListSuccess()) {
      return this.displayToast(true);
    }

    const wishList: WishList | undefined = this.isEditWishList 
      ? getEditWishListData(store.getState() as StoreState) 
      : getCreateWishListData(store.getState() as StoreState);

    this.isEditWishList 
      ? store.dispatch(resetEditWishList()) 
      : store.dispatch(resetCreateWishList());

    this.isWishListItemPage 
      ? store.dispatch(fetchWishListByWishListId(this.wishListId))
      : store.dispatch(fetchAllWishLists());

    this.closeOffCanvasIfExist();

    return this.displayToast(false, wishList);
  }

  /**
   * Displays a toast depending on the wish list creation status.
   */
  private displayToast(isError: boolean, wishList?: WishList): void {
    const toastGlobalContainer: HTMLElement | undefined = getGlobalToastContainer();
    if (!toastGlobalContainer) {
      return;
    }

    if (isError) {
      render(
        renderErrorToast({
          error: this.isEditWishList ? ErrorToastWishList.UPDATING_WISH_LIST : ErrorToastWishList.CREATING_WISH_LIST,
          displayLoadingTemplate: true,
          autoHide: false,
          hideClose: true,
        }),
        toastGlobalContainer
      );
      return;
    }

    this.isEditWishList 
      ? render(renderEditWishListSuccessToast(wishList), toastGlobalContainer) 
      : render(renderCreateWishListSuccessToast(wishList), toastGlobalContainer);
  }

  /**
   * Handles off-canvas actions if the off-canvas ID is set.
   */
  private handleOffCanvasActions(state: StoreState): void {
    if (!this.offCanvasId) {
      return;
    }

    focusOffCanvasElement(this.shadowRoot!, state);
    if (!this.isEditWishList && shouldClearOffCanvasElements(this.offCanvasId, state)) {
      this.wishListName = '';
    }
  }

  /**
   * Closes the off-canvas if it exists.
   */
  private closeOffCanvasIfExist(): void {
    if (this.offCanvasId) {
      store.dispatch(triggerCloseOffCanvas({offCanvasId: this.offCanvasId}));
    }
  } 
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-create-wish-list': CreateWishList;
  }
}